import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { RowAction } from '@backrs_ui/grow-ui/src/components/RowActionMenu/RowActionMenu';
import type { MemberSeat } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { SEAT_KIND } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  cohortSeats,
  knownSeats,
  movableSeats,
  revocableSeats,
  rosterLabel,
  type SeatHolder,
} from './memberSeats';
import type { SeatActionKind } from './useSeatActions';

const { seatActions } = appStrings.modules.coachingDashboard;

const cohortRosters = (holder: SeatHolder): MemberSeat[] => cohortSeats(knownSeats(holder.seats));

const cohortSeatsOnOneRoster = (holder: SeatHolder): number => {
  const seats = cohortRosters(holder);

  return new Set(seats.map((seat) => seat.roster_id)).size > 1 ? 0 : seats.length;
};

const cohortWithheld = (holder: SeatHolder): string => {
  const seats = cohortRosters(holder);
  const rosters = Array.from(new Set(seats.map(rosterLabel)));

  return rosters.length > 1
    ? seatActions.withheld.cohortSeatsSpanRosters(rosters.join(', '))
    : seatActions.withheld.noCohortSeat;
};

type Entry = Readonly<{
  kind: SeatActionKind;
  label: string;
  eligible?: (holder: SeatHolder) => number;
  withheld?: string | ((holder: SeatHolder) => string);
}>;

const STUDENT_ENTRIES: readonly Entry[] = [
  {
    kind: 'assignCoaches',
    label: seatActions.assignCoaches,
  },
  {
    kind: 'move',
    label: seatActions.move,
    eligible: (holder) => movableSeats(knownSeats(holder.seats)).length,
    withheld: seatActions.withheld.noMovableSeat,
  },
  {
    kind: 'addCohort',
    label: seatActions.addToCohort,
    eligible: cohortSeatsOnOneRoster,
    withheld: cohortWithheld,
  },
  {
    kind: 'removeCohort',
    label: seatActions.removeFromCohort,
    eligible: cohortSeatsOnOneRoster,
    withheld: cohortWithheld,
  },
  {
    kind: 'revoke',
    label: seatActions.revoke,
    eligible: (holder) => revocableSeats(knownSeats(holder.seats), SEAT_KIND.student).length,
    withheld: seatActions.withheld.noStudentSeat,
  },
];

const COACH_ENTRIES: readonly Entry[] = [
  {
    kind: 'revoke',
    label: seatActions.revoke,
    eligible: (holder) => revocableSeats(knownSeats(holder.seats), SEAT_KIND.coach).length,
    withheld: seatActions.withheld.noCoachSeat,
  },
];

/**
 * Every entry is always present. One withheld with its reason read out says the door
 * exists and why it cannot be taken; an absent entry says only that nothing is here.
 */
const build = (
  entries: readonly Entry[],
  holder: SeatHolder,
  onSelect: (kind: SeatActionKind) => void,
): RowAction[] =>
  entries.map((entry) => {
    const id = `coaching-dashboard-seat-${entry.kind}-${holder.id}`;
    const { eligible } = entry;

    if (eligible === undefined) {
      return { id, label: entry.label, onSelect: () => onSelect(entry.kind) };
    }

    // Unknown before empty: a read that did not carry their seats cannot say they hold
    // none, and a reason an operator acts on must not be one we invented.
    if (!holder.seats.known) {
      return {
        id,
        label: entry.label,
        onSelect: () => onSelect(entry.kind),
        disabled: true,
        disabledReason: seatActions.withheld.seatsUnknown,
      };
    }

    const count = eligible(holder);
    const withheld = typeof entry.withheld === 'function' ? entry.withheld(holder) : entry.withheld;
    const reason = holder.seats.seats.length === 0 ? seatActions.withheld.noSeat : withheld;

    return {
      id,
      label: entry.label,
      onSelect: () => onSelect(entry.kind),
      disabled: count === 0,
      disabledReason: count === 0 ? reason : undefined,
    };
  });

export const studentRowActions = (
  holder: SeatHolder,
  onSelect: (kind: SeatActionKind) => void,
): RowAction[] => build(STUDENT_ENTRIES, holder, onSelect);

export const coachRowActions = (
  holder: SeatHolder,
  onSelect: (kind: SeatActionKind) => void,
): RowAction[] => build(COACH_ENTRIES, holder, onSelect);

type BulkEntry = Readonly<{
  kind: SeatActionKind;
  id: string;
  label: string;
  eligible?: (seats: readonly MemberSeat[]) => readonly MemberSeat[];
  withheld?: string;
}>;

const BULK_ENTRIES: readonly BulkEntry[] = [
  {
    kind: 'assignCoaches',
    id: 'coaching-dashboard-bulk-assign-btn',
    label: seatActions.assignCoaches,
  },
  {
    kind: 'move',
    id: 'coaching-dashboard-bulk-move-btn',
    label: seatActions.bulkMove,
    eligible: movableSeats,
    withheld: seatActions.withheld.bulkNoMovableSeat,
  },
];

/**
 * Open while ANY of the selection can be acted on: the ids that cannot come back named in
 * the report rather than silently thinning the batch. Shut only where the door has nothing
 * at all to act on, and then it says so.
 */
export const bulkSeatActions = (
  holders: readonly SeatHolder[],
  onSelect: (kind: SeatActionKind) => void,
): RowAction[] =>
  BULK_ENTRIES.map((entry) => {
    const { eligible } = entry;

    if (eligible === undefined) {
      return { id: entry.id, label: entry.label, onSelect: () => onSelect(entry.kind) };
    }

    // One unreadable holder shuts the door for the whole selection: a batch that quietly
    // dropped them would report on fewer people than were selected.
    const isUnknown = holders.some((holder) => !holder.seats.known);
    const isOffered =
      !isUnknown && holders.some((holder) => eligible(knownSeats(holder.seats)).length > 0);

    return {
      id: entry.id,
      label: entry.label,
      onSelect: () => onSelect(entry.kind),
      disabled: !isOffered,
      disabledReason: isOffered
        ? undefined
        : isUnknown
          ? seatActions.withheld.bulkSeatsUnknown
          : entry.withheld,
    };
  });
